'use client';

import { CartQuery, useBasketCreateMutation } from 'lib/api';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import axios from 'redaxios';
import { toast } from 'react-hot-toast';
import { useCartStore } from 'store/cart';
import { tw } from 'twind';
import { ActionBar } from '../ActionBar';
import { CartItem } from './CartItem';
import { ModalLogin } from './ModalLogin';
import { Price } from '../Price';
import Testimonial from './Testimonial';
import useQueryString from '../../hooks/useQueryString';
import { useRudderstack } from 'analytics/RudderStack';
import CheckoutLoading from '../Animation/CheckoutLoading';
import { BrandLogo } from './BrandLogo';
import { DiscountModal } from '@/components/DiscountModal';
import useDiscountPopupTimeout from '../../hooks/useDiscountPopupTimeout';
import ConfettiExplosion from 'react-confetti-explosion';
import { PromoMessage } from '@/components/NewCart/PromoMessage';
import usePrice from '../../hooks/usePrice';
import ProductCard from '@/components/lib/ProductCard/ProductCard';
import styles from '@/components/pages/Products/Products.module.css';
import { Product } from '@/interfaces/index';

export const Avatar: React.FC<{
  src: string;
  alt?: string;
  className?: string;
  username: string;
  type: 'twitter' | 'instagram';
}> = (props) => (
  <a href="">
    <img className={tw`inline-block h-14 w-14 rounded-full border border-black ${props.className}`} src={props.src} alt={props.alt} />
  </a>
);

export const CreatorHandle: React.FC<{
  handle: string;
  full_url: string;
}> = ({ handle, full_url }) => {
  const { theme } = useCartStore();

  return (
    <a href={full_url} className={tw`font-bold text-${theme}-primary`} rel="nofollow noreferrer" target="_blank">
      <span>@{handle}</span>
    </a>
  );
};

export const NewCart: React.FC<{ cart: CartQuery['Cart']; referer: string, products: Product[] }> = ({ cart, referer, products }) => {
  console.log(cart);

  const router = useRouter();
  const [queryParams, setQueryParams] = useQueryString();
  const { underAge, theme, loaded, setCart, selectedVariants, selectedQuantities, discountsApplied, applyDiscounts } = useCartStore();
  const [, createBasket] = useBasketCreateMutation();
  const [checkoutIsLoading, setCheckoutIsLoading] = React.useState(false);
  const [showCheckoutLoading, setShowCheckoutLoading] = React.useState(false);
  const [usesDiscountModal, setUsesDiscountModal] = useState(false);
  const [discountModal, setDiscountModal] = useState(false);
  const rudder = useRudderstack();

  useEffect(() => {
    // initialize our store (once)
    if (loaded) {
      return;
    }
    setCart(cart);
  }, [loaded, setCart, cart]);

  useDiscountPopupTimeout(cart, String(queryParams?.origin), setDiscountModal, setUsesDiscountModal);

  useLayoutEffect(() => {
    rudder.track('cart-visit', {
      cart_id: cart.id,
      step: 1,
    });
  }, [rudder, cart.id]);

  const price = usePrice();
  const confettiExploding = useMemo(() => {
    if (!discountsApplied) return false;

    return price.amount !== price.old_amount;
  }, [discountsApplied, price]);

  const isSkipAuthorization = useMemo(() => {
    if (queryParams?.origin === 'sms') return true;
    if (!cart.popup_discounts) return false;

    return discountsApplied;
  }, [cart.popup_discounts, discountsApplied, queryParams?.origin]);

  const getBasket = async () =>
    await createBasket({
      input: {
        cart_id: cart.id,
        origin: referer,
        discounts_applied: discountsApplied,
        items: cart.items
          .map((item) => ({
            quantity: selectedQuantities[item.id],
            sku: selectedVariants[item.id].sku,
          }))
          .filter(({ quantity }) => quantity > 0),
      },
    });

  const markSolds = async () => {
    if (theme !== 'OneItem') {
      return;
    }

    const itemIds = cart.items.map((item) => item.id).filter((id) => selectedQuantities[id] > 0);
    await axios.post('/api/inventory', { cartId: cart.id, itemIds });
  };

  const getShopUrl = () => {
    const items = Array.from(Object.entries(selectedVariants)).reduce((acc, [id, { id: variantId }]) => {
      const quantity = selectedQuantities[id];
      if (quantity) {
        acc = acc.concat(`${variantId.split('/').pop()}:${quantity},`);
      }
      return acc;
    }, '');
    const shopOrigin = new URL(cart.items.at(0)?.productUrl).origin;
    return `${shopOrigin}/discount/${cart.discount_code}?redirect=%2Fcart/${items}`;
  };

  const getBasketExternalUrl = async () => {
    const basket = await getBasket();
    return basket.data?.BasketCreate?.external_url;
  };

  const proceedToCheckout = React.useCallback(async () => {
    try {
      if (queryParams?.origin === 'sms') {
        setCheckoutIsLoading(true);
      }

      await markSolds();
      const url = theme === 'NBC' ? getShopUrl() : await getBasketExternalUrl();

      if (url) {
        setShowCheckoutLoading(true);
        router.push(url);
        rudder.track('checkout-visit', {
          cart_id: cart.id,
          step: 3,
        });
      } else {
        setQueryParams({ step: 'auth' });
      }
    } catch (e) {
      console.error(e);
      toast.error('Oops. Something went wrong. Please try again.');
    }
  }, [createBasket, cart, selectedQuantities, selectedVariants, router, setQueryParams, setShowCheckoutLoading]);

  const authenticate = React.useCallback(async () => {
    try {
      setCheckoutIsLoading(true);

      const { data } = await axios.get('/api/auth/check');

      if (data.user) {
        proceedToCheckout();
      } else {
        setQueryParams({ step: 'auth' });
      }
    } catch (e) {
      setQueryParams({ step: 'auth' });
    }
  }, [setQueryParams, proceedToCheckout, setCheckoutIsLoading, cart.requires_auth]);

  const closeLoginModal = React.useCallback(() => {
    setCheckoutIsLoading(false);
    setQueryParams({ step: undefined });
  }, [setQueryParams, setCheckoutIsLoading]);

  const handleDiscountModal = useCallback(() => {
    setDiscountModal(false);
    applyDiscounts();
  }, [applyDiscounts]);

  const onPromoMessageClick = useCallback(() => {
    if (!cart.popup_discounts) {
      return;
    }

    setDiscountModal(true);
  }, [cart, setDiscountModal]);

  const taxShippingLabel = cart?.tax_shipping_label ?? '+ tax & shipping';
  const nextDescription = (
    <>
      <div className={tw`text-lg`}>
        <Price price={price} />
        {confettiExploding && <ConfettiExplosion force={0.6} particleCount={40} duration={2500} width={200} height={400} />}
      </div>
      <div className={tw`text-xs text-right text-${theme}-primary`}>{taxShippingLabel}</div>
    </>
  );

  const creatorHandle = cart.creator.socials?.[0]?.handle;
  let nextAction = authenticate;
  if (isSkipAuthorization) {
    nextAction = proceedToCheckout;
  }

  const { unlimited_products_ids } = cart;

  const unlimitedProductsIds = unlimited_products_ids ? unlimited_products_ids.split(',') : [];

  console.log(unlimitedProductsIds);

  const getVisibleProducts = () => {
    if (!products || products.length === 0) {
      return [];
    }

    if (cart.creator?.slug === 'happyisles' || cart.creator?.slug === 'tobiasauctions') {
      return products;
    }

    return products.slice(0, 1);
  }

  return (
    <>
      <form className={tw`border-t-[6px] border-${theme} theme-${theme}`}>
        <div className={tw`sm:px-[24px]`}>
          {cart.shop?.avatar && <BrandLogo src={cart.shop?.avatar} alt={cart.shop?.display_name} />}

          <div className={tw`px-[6px] pb-24`}>
            <div className={tw`flex flex-col align-middle items-center pb-4`}>
              <Avatar
                className={tw`rounded-full my-[12px] border-black border-2`}
                src={cart.creator.avatar}
                alt="avatar"
                username="maktouch"
                type="twitter"
              />

              <CreatorHandle handle={creatorHandle} full_url={cart.creator.socials?.[0]?.full_url} />

              {cart.testimonial && <Testimonial quote={cart.testimonial} />}
            </div>

            {loaded && (
              <div className={tw`flex flex-col items-center`}>
                <PromoMessage onPromoMessageClick={onPromoMessageClick} />

                { products.length > 0 ?
                  <div className={tw`w-full max-w-3xl`}>
                    <div className={styles.labelAuctions}>{getVisibleProducts().length === 1 ? 'Auction Item' : 'Auction Items'}</div>
                  </div> :
                  null }

                {getVisibleProducts().map((product) => (
                  <div className={tw`w-full max-w-3xl mb-[24px]`} key="auction">
                    <ProductCard key={product.id} cartSlug={cart.slug} product={product} />
                  </div>
                ))}

                { cart.items.length > 0 ? <div className={tw`w-full max-w-3xl`}>
                    <div className={styles.labelBuyNow}>Buy Now Items</div>
                  </div>
                  : null }

                  {cart.items.map((item) => (
                  <CartItem
                    key={item.id}
                    item={item}
                    unlimitedProductsIds={unlimitedProductsIds}
                    applyDiscounts={discountsApplied}
                    usesDiscountModal={usesDiscountModal}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {cart.items.length > 0 ?
          <ActionBar
            nextLabel="Checkout"
            nextAction={nextAction}
            nextDisabled={price.amount === 0 || !loaded || underAge}
            nextLoading={checkoutIsLoading}
            nextDescription={nextDescription}
            buttonStyle={{ width: 100 }}
          /> :
          null}
        <ModalLogin
          isOpen={queryParams.step === 'auth'}
          requestClose={closeLoginModal}
          onSuccess={proceedToCheckout}
          cartId={cart.id}
          creatorId={cart.creator.id}
          creatorName={creatorHandle}
          requireAuth={cart.requires_auth}
        />
        <DiscountModal
          isOpen={discountModal}
          requestClose={() => setDiscountModal(false)}
          onSuccess={handleDiscountModal}
          cartId={cart.id}
          creatorId={cart.creator.id}
          message={cart.discount_message_template ?? 'Your phone number'}
        />
      </form>

      {checkoutIsLoading && showCheckoutLoading && <CheckoutLoading theme={theme} brandName={cart.shop?.display_name} />}
    </>
  );
};
